/**
 * applyArFormulaResultsToCells
 *
 * Read-only projection: returns a copy of a row's cells with freshly computed
 * formula results placed where server consumers already look — plain targets in
 * BOTH `calcInput` (the persisted slot) and `value` (what every workflow reader
 * and the agent projection read), dotted targets in the composite sub-value.
 * A `null` result clears the slot, mirroring the trigger's delete. Consumers:
 * loadWorkflowTableRows (workflow value sources, Signify), the agent's
 * getTableRowsOp, `buildArTableSheetSchema` (both XLSX export paths) and
 * `arCellClipboard` (grid copy). NEVER persist the result — writing it back would store a
 * derived number into a formula target's `value`, which no writer owns.
 * Always a fresh top-level object, the skipped path included: the caller's
 * cells object is never returned, so a consumer that mutates what it got back
 * cannot reach into a row snapshot the engine was only reading.
 */

import type { ArCell, TypeId } from '../System.Artifacts.types';
import type { ArRowFormulaComputation } from './computeArRowFormulas';

type CompositeValue = { fields?: Record<string, { value?: unknown }> };

function applyArFormulaResultsToCells(
  cells: Record<TypeId, ArCell> | undefined,
  computation: ArRowFormulaComputation,
): Record<TypeId, ArCell> {
  const base = cells ?? {};
  if (computation.skipped) return { ...base };

  return [...computation.results].reduce<Record<TypeId, ArCell>>((next, [typeId, result]) => {
    const dotIndex = (typeId as string).indexOf('.');
    if (dotIndex === -1) {
      return {
        ...next,
        [typeId]: {
          ...next[typeId],
          value: result === null ? undefined : result,
          calcInput: result === null ? undefined : result,
        },
      };
    }

    const root = (typeId as string).slice(0, dotIndex) as TypeId;
    const subFieldId = (typeId as string).slice(dotIndex + 1);
    const cell = next[root];
    const composite = (typeof cell?.value === 'object' && cell.value !== null
      ? cell.value
      : {}) as CompositeValue;
    return {
      ...next,
      [root]: {
        ...cell,
        value: {
          ...composite,
          fields: {
            ...composite.fields,
            [subFieldId]: {
              ...composite.fields?.[subFieldId],
              value: result === null ? undefined : result,
            },
          },
        },
      } as ArCell,
    };
  }, { ...base });
}

export { applyArFormulaResultsToCells };
